import React, { useEffect, useRef, useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import logo from "../Images/logo.png"
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../store';
import { logout } from "../Reducers/Organization/AuthReducer";
import TrialNotice from "../Components/TrialNotice"
import { ToastContainer } from 'react-toastify';
import VerifyNotice from '../Components/VerifyNotice';
import EmailVerificationForm from '../Components/EmailVerificationForm';
import { useTranslation } from 'react-i18next';
import { HiCog6Tooth, HiArrowRightOnRectangle, HiUser } from 'react-icons/hi2';

interface sideBarProps {
  children: React.ReactNode
}

const Sidebar: React.FC<sideBarProps> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [modalHidden, setModalHidden] = useState(true);
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { i18n, t } = useTranslation();
  const org = useSelector((state: RootState) => state.auth.org);
  const menuRef = useRef<HTMLDivElement>(null);

  const handleChangeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
    document.documentElement.dir = lng === 'ar' ? 'rtl' : 'ltr';
  };

  const showVerificationModal = () => setModalHidden(false);
  const toggleSidebar = () => setSidebarOpen(prev => !prev);
  const closeSidebar = () => setSidebarOpen(false);
  const logoutOrganization = () => dispatch(logout());
  const isRtl = i18n.language === 'ar';

  // Close user menu on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <>
      {/* ── Mobile top bar ── */}
      <div className="md:hidden flex items-center justify-between px-4 py-3 sticky top-0 z-30 bg-white border-b border-brand-100 shadow-sm">
        <img src={logo} className="h-8 w-auto" alt="logo" />
        <div className="flex items-center gap-2">
          {/* Language switcher (mobile) */}
          <div className="flex rounded-xl overflow-hidden border border-brand-100">
            <button
              onClick={() => handleChangeLanguage('en')}
              className={`px-3 py-1.5 text-xs font-semibold transition-colors ${i18n.language === 'en' ? 'bg-brand-600 text-white' : 'bg-white text-brand-700 hover:bg-brand-50'}`}
            >EN</button>
            <button
              onClick={() => handleChangeLanguage('ar')}
              className={`px-3 py-1.5 text-xs font-semibold transition-colors ${i18n.language === 'ar' ? 'bg-brand-600 text-white' : 'bg-white text-brand-700 hover:bg-brand-50'}`}
            >ع</button>
          </div>

          {/* User avatar button (mobile) */}
          <div ref={menuRef} className="relative">
            <button
              onClick={() => setUserMenuOpen(prev => !prev)}
              className="w-9 h-9 rounded-full bg-brand-100 flex items-center justify-center text-brand-700 hover:bg-brand-200 transition"
            >
              <HiUser className="w-4 h-4" />
            </button>
            {userMenuOpen && (
              <div className="absolute end-0 top-11 w-52 bg-white rounded-2xl border border-brand-100 shadow-xl py-1.5 z-50">
                <div className="px-4 py-2.5 border-b border-gray-100">
                  <p className="text-xs font-bold text-gray-900 truncate">{org?.name ?? "—"}</p>
                  <p className="text-xs text-gray-400 truncate">{org?.email}</p>
                </div>
                <button
                  onClick={() => { navigate('/Settings'); setUserMenuOpen(false); }}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-brand-50 transition"
                >
                  <HiCog6Tooth className="w-4 h-4 text-brand-500" />
                  {t("settings") || "الإعدادات"}
                </button>
                <button
                  onClick={() => { setUserMenuOpen(false); logoutOrganization(); }}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-500 hover:bg-red-50 transition"
                >
                  <HiArrowRightOnRectangle className="w-4 h-4" />
                  {t("logout")}
                </button>
              </div>
            )}
          </div>
          {/* Hamburger */}
          <button
            onClick={toggleSidebar}
            className="p-2 rounded-xl text-brand-700 hover:bg-brand-50 transition-colors"
            aria-label="Toggle sidebar"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {sidebarOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>
      </div>

      {/* Backdrop */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-30 md:hidden" onClick={closeSidebar} />
      )}

      {/* ── Sidebar ── */}
      <aside
        className={`fixed top-0 ${isRtl ? 'right-0' : 'left-0'} z-40 w-56 h-screen transition-transform duration-300 bg-white border-e border-brand-100 ${
          sidebarOpen ? 'translate-x-0' : isRtl ? 'translate-x-full' : '-translate-x-full'
        } md:translate-x-0`}
      >
        <div className="h-full flex flex-col px-3 py-6 overflow-y-auto">
          {/* Logo */}
          <div className="flex justify-center mb-8 px-2">
            <img src={logo} className="h-10 w-auto" alt="logo" />
          </div>

          {/* Nav items */}
          <ul className="flex-1 space-y-1">
            {children}
          </ul>

          <TrialNotice />
        </div>
      </aside>

      {/* ── Main content ── */}
      <div className={`${isRtl ? 'md:mr-56' : 'md:ml-56'} min-h-screen bg-brand-50 flex flex-col`}>
        <ToastContainer position="top-right" autoClose={3000} />

        {/* ── Desktop top nav ── */}
        <header className="hidden md:flex items-center justify-between px-6 py-3 bg-white border-b border-brand-100 sticky top-0 z-20">
          <div /> {/* spacer — breadcrumbs can go here later */}
          <div className="flex items-center gap-3" ref={menuRef}>
            {/* Language switcher (desktop) */}
            <div className="flex rounded-xl overflow-hidden border border-brand-100">
              <button
                onClick={() => handleChangeLanguage('en')}
                className={`px-3.5 py-1.5 text-xs font-semibold transition-colors ${i18n.language === 'en' ? 'bg-brand-600 text-white' : 'bg-white text-brand-700 hover:bg-brand-50'}`}
              >EN</button>
              <button
                onClick={() => handleChangeLanguage('ar')}
                className={`px-3.5 py-1.5 text-xs font-semibold transition-colors ${i18n.language === 'ar' ? 'bg-brand-600 text-white' : 'bg-white text-brand-700 hover:bg-brand-50'}`}
              >عربي</button>
            </div>

            {/* User menu button */}
            <button
              onClick={() => setUserMenuOpen(prev => !prev)}
              className="flex items-center gap-2.5 px-3 py-2 rounded-xl hover:bg-brand-50 transition group"
            >
              <div className="w-8 h-8 rounded-full bg-brand-100 flex items-center justify-center text-brand-700 group-hover:bg-brand-200 transition">
                <HiUser className="w-4 h-4" />
              </div>
              <div className={`text-start ${isRtl ? 'text-right' : ''}`}>
                <p className="text-xs font-bold text-gray-900 leading-tight max-w-[120px] truncate">{org?.name ?? "—"}</p>
                <p className="text-xs text-gray-400 leading-tight max-w-[120px] truncate">{org?.email}</p>
              </div>
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {/* Dropdown */}
            {userMenuOpen && (
              <div className="absolute end-4 top-14 w-56 bg-white rounded-2xl border border-brand-100 shadow-xl py-1.5 z-50">
                <div className="px-4 py-3 border-b border-gray-100">
                  <p className="text-xs font-bold text-gray-900 truncate">{org?.name}</p>
                  <p className="text-xs text-gray-400 truncate">{org?.email}</p>
                </div>
                <button
                  onClick={() => { navigate('/Settings'); setUserMenuOpen(false); }}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-brand-50 transition"
                >
                  <HiCog6Tooth className="w-4 h-4 text-brand-500" />
                  {t("settings") || "الإعدادات"}
                </button>
                <div className="my-1 border-t border-gray-100" />
                <button
                  onClick={logoutOrganization}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-500 hover:bg-red-50 transition"
                >
                  <HiArrowRightOnRectangle className="w-4 h-4" />
                  {t("logout")}
                </button>
              </div>
            )}
          </div>
        </header>

        <VerifyNotice showModal={showVerificationModal} />

        {/* Email verification modal */}
        {!modalHidden && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={() => setModalHidden(true)}>
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-md mx-4 p-6 relative" onClick={e => e.stopPropagation()}>
              <button
                onClick={() => setModalHidden(true)}
                className="absolute top-4 end-4 w-8 h-8 rounded-lg flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition"
              >✕</button>
              <EmailVerificationForm />
            </div>
          </div>
        )}

        <div className="p-4 md:p-6 flex-1">
          <div className="max-w-7xl mx-auto">
            <Outlet />
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
